import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Stack,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from '@mui/material';
import ViewSidebarRounded from '@mui/icons-material/ViewSidebarRounded';
import HomeRounded from '@mui/icons-material/HomeRounded';
import TrendingUpRounded from '@mui/icons-material/TrendingUpRounded';
import { SnackbarContext } from '../../ui/SnackbarProvider';
import { ModeContext } from '../../theme/ThemeProvider';
import { fetchTenantSettings, saveTenantSettings, getTenantIdFromStorage } from '../../services/tenantSettingsService';
import SettingsPageHeader from './components/SettingsPageHeader';
import StickySaveBar from './components/StickySaveBar';

type SidebarPosition = 'left' | 'right';
type DefaultHomePage = 'copilot' | 'dashboards' | 'spaces';

const HOME_PAGE_OPTIONS: Array<{ value: DefaultHomePage; label: string; helper: string }> = [
  { value: 'copilot', label: 'Copilot', helper: 'Open the chat home page first.' },
  { value: 'dashboards', label: 'Dashboards', helper: 'Land on the dashboards index.' },
  { value: 'spaces', label: 'Spaces', helper: 'Open shared spaces and saved queries.' },
];

const SUPPORTED_CALENDARS: Array<{ code: string; label: string }> = [
  { code: 'AE', label: 'United Arab Emirates' },
  { code: 'AU', label: 'Australia' },
  { code: 'BR', label: 'Brazil' },
  { code: 'CA', label: 'Canada' },
  { code: 'DE', label: 'Germany' },
  { code: 'FR', label: 'France' },
  { code: 'GB', label: 'United Kingdom' },
  { code: 'IN', label: 'India' },
  { code: 'JP', label: 'Japan' },
  { code: 'MX', label: 'Mexico' },
  { code: 'SG', label: 'Singapore' },
  { code: 'US', label: 'United States' },
  { code: 'ZA', label: 'South Africa' },
];

interface SectionHeaderProps {
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
}

function SectionHeader({ icon, title, subtitle }: SectionHeaderProps) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
      <Box
        sx={(t) => ({
          width: 32,
          height: 32,
          borderRadius: `${t.ds.radius.sm}px`,
          bgcolor: t.tokens.brand.primarySoft,
          color: t.tokens.brand.primary,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        })}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ fontSize: '1rem', lineHeight: 1.3 }}>
          {title}
        </Typography>
        {subtitle ? (
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem', lineHeight: 1.45, mt: 0.25 }}>
            {subtitle}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
}

export default function WorkspaceSettingsPage() {
  const { notify } = React.useContext(SnackbarContext);
  const { refreshBranding } = React.useContext(ModeContext);
  const tenantId = React.useMemo(() => getTenantIdFromStorage(), []);

  const [sidebarPosition, setSidebarPosition] = React.useState<SidebarPosition>('left');
  const [defaultHomePage, setDefaultHomePage] = React.useState<DefaultHomePage>('copilot');
  const [holidayCalendars, setHolidayCalendars] = React.useState<string[]>([]);

  const [loading, setLoading] = React.useState<boolean>(false);
  const [saving, setSaving] = React.useState<boolean>(false);

  const hydrate = React.useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const s = await fetchTenantSettings(tenantId);
      const branding = (s['BRANDING'] || {}) as Record<string, any>;
      if (branding.sidebarPosition === 'left' || branding.sidebarPosition === 'right') {
        setSidebarPosition(branding.sidebarPosition);
      }
      const home = branding.defaultHomePage;
      if (home === 'copilot' || home === 'dashboards' || home === 'spaces') {
        setDefaultHomePage(home);
      }
      const forecast = s['FORECAST'];
      if (forecast && Array.isArray(forecast.holiday_calendars)) {
        setHolidayCalendars(forecast.holiday_calendars as string[]);
      }
    } catch (e: any) {
      notify(e?.message || 'Failed to load workspace settings', 'error');
    } finally {
      setLoading(false);
    }
  }, [tenantId, notify]);

  React.useEffect(() => { hydrate(); }, [hydrate]);

  const save = async () => {
    if (!tenantId) {
      notify('Missing tenant', 'error');
      return;
    }
    setSaving(true);
    try {
      // BRANDING is shared with the Branding page — merge into the existing
      // sub-object so we don't overwrite colors/logo on save.
      await saveTenantSettings(tenantId, (current) => ({
        BRANDING: {
          ...((current.BRANDING as Record<string, any>) || {}),
          sidebarPosition,
          defaultHomePage,
        },
        FORECAST: {
          ...((current.FORECAST as Record<string, any>) || {}),
          holiday_calendars: holidayCalendars,
        },
      }));
      notify('Workspace settings saved', 'success');
      // Sidebar position lives in branding state — refresh the cache so the
      // layout reflects the new position immediately.
      refreshBranding();
    } catch (e: any) {
      notify(e?.message || 'Failed to save workspace settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  const cardSx = { mb: 3 };
  const cardContentSx = {
    px: { xs: 2, sm: 3 },
    py: { xs: 2, sm: 3 },
    '&:last-child': { pb: { xs: 2, sm: 3 } },
  };

  return (
    <Box sx={{ width: '100%' }}>
      <SettingsPageHeader description="Defaults that shape how everyone in your tenant uses the product — navigation, Copilot governance, and forecasting." />

      {/* Navigation */}
      <Card sx={cardSx}>
        <CardContent sx={cardContentSx}>
          <SectionHeader
            icon={<ViewSidebarRounded sx={{ fontSize: 18 }} />}
            title="Navigation"
            subtitle="Where the sidebar lives and which page users land on after login."
          />
          <Stack spacing={2.5}>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontSize: '0.875rem' }}>
                Sidebar position
              </Typography>
              <ToggleButtonGroup
                value={sidebarPosition}
                exclusive
                onChange={(_e, val: SidebarPosition | null) => {
                  if (val) setSidebarPosition(val);
                }}
                size="small"
                sx={{ width: { xs: '100%', sm: 'auto' } }}
              >
                <ToggleButton value="left" sx={{ px: 3, textTransform: 'none', flex: { xs: 1, sm: 'none' } }}>
                  Left
                </ToggleButton>
                <ToggleButton value="right" sx={{ px: 3, textTransform: 'none', flex: { xs: 1, sm: 'none' } }}>
                  Right
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <HomeRounded sx={(t) => ({ fontSize: 16, color: t.tokens.text.tertiary })} />
                <Typography variant="subtitle2" sx={{ fontSize: '0.875rem' }}>
                  Default home page
                </Typography>
              </Box>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.5}
                flexWrap="wrap"
                sx={{ rowGap: 1.5 }}
              >
                {HOME_PAGE_OPTIONS.map((opt) => {
                  const selected = defaultHomePage === opt.value;
                  return (
                    <Button
                      key={opt.value}
                      variant={selected ? 'contained' : 'outlined'}
                      onClick={() => setDefaultHomePage(opt.value)}
                      sx={{
                        flex: { xs: 'none', sm: 1 },
                        minWidth: 0,
                        textTransform: 'none',
                        fontWeight: 600,
                        justifyContent: 'flex-start',
                        textAlign: 'left',
                        py: 1.25,
                      }}
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                        <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, lineHeight: 1.3 }}>{opt.label}</Typography>
                        <Typography
                          sx={{
                            fontSize: '0.75rem',
                            color: selected ? 'inherit' : 'text.secondary',
                            opacity: selected ? 0.85 : 1,
                            fontWeight: 400,
                            lineHeight: 1.35,
                            mt: 0.25,
                            textTransform: 'none',
                          }}
                        >
                          {opt.helper}
                        </Typography>
                      </Box>
                    </Button>
                  );
                })}
              </Stack>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Forecasting */}
      <Card sx={cardSx}>
        <CardContent sx={cardContentSx}>
          <SectionHeader
            icon={<TrendingUpRounded sx={{ fontSize: 18 }} />}
            title="Forecasting"
            subtitle="Holiday calendars used to enrich forecasts with public-holiday effects. Pick all countries where your business operates."
          />
          <FormControl fullWidth>
            <InputLabel id="holiday-calendars-label">Holiday calendars</InputLabel>
            <Select
              labelId="holiday-calendars-label"
              label="Holiday calendars"
              multiple
              value={holidayCalendars}
              onChange={(e: { target: { value: string | string[] } }) => {
                const val = e.target.value;
                setHolidayCalendars(typeof val === 'string' ? val.split(',') : val);
              }}
              renderValue={(selected: unknown) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(selected as string[]).map((code) => {
                    const cal = SUPPORTED_CALENDARS.find((c) => c.code === code);
                    return (
                      <Chip
                        key={code}
                        label={cal ? `${cal.label} (${code})` : code}
                        size="small"
                      />
                    );
                  })}
                </Box>
              )}
            >
              {SUPPORTED_CALENDARS.map((cal) => (
                <MenuItem key={cal.code} value={cal.code}>
                  {cal.label} ({cal.code})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </CardContent>
      </Card>

      <StickySaveBar
        description="Sidebar and home-page changes apply immediately on save."
        onSave={save}
        onReset={hydrate}
        saving={saving}
        disabled={loading}
      />
    </Box>
  );
}
